import { useEffect, useState } from "react";
import { Check, Eye, Minus } from "lucide-react";
import { http } from "../../api";
import CandidateModal from "./CandidateModal";

const STAGES = [["", "All"], ["chat", "In chat"], ["profile", "Filling details"], ["language", "Language test"], ["listening", "Listening test"], ["agreements", "Signing agreements"], ["shortlisted", "Shortlisted"]];

const Tick = ({ ok, label, testid }) => <span className={`badge ${ok ? "success" : ""}`} data-testid={testid}>{ok ? <Check size={12} /> : <Minus size={12} />}{label ? ` ${label}` : ""}</span>;

function TestCell({ items, status }) {
  const required = items.filter(i => i.required);
  if (!required.length) return <small className="muted">Not set up</small>;
  return (
    <div className="test-cell">
      {required.map(i => (
        <span key={i.language} className={`badge ${!i.attempt ? "" : i.attempt.status !== "SUBMITTED" ? "warning" : i.attempt.passed ? "success" : "danger"}`}>
          {i.language}: {i.attempt && i.attempt.status === "SUBMITTED" ? `${i.attempt.percent}%` : i.attempt ? "in progress" : "—"}
        </span>
      ))}
      {status === "passed" && <Check size={13} color="var(--success)" />}
    </div>
  );
}

export default function CandidateTable() {
  const [data, setData] = useState(null);
  const [language, setLanguage] = useState("");
  const [stage, setStage] = useState("");
  const [search, setSearch] = useState("");
  const [langs, setLangs] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => http.get("/admin/creators", { params: { language, stage, search } }).then(r => setData(r.data));
  useEffect(() => { load(); }, [language, stage, search]);
  useEffect(() => { http.get("/creator/config").then(r => setLangs(["English", ...r.data.regional_languages])); }, []);
  if (!data) return <div className="loading">Loading candidates…</div>;
  const c = data.counts;

  return (
    <>
      <div className="stats" data-testid="creator-stats">
        <div className="stat-card"><span>Candidates</span><strong data-testid="stat-total">{c.total || 0}</strong></div>
        <div className="stat-card"><span>In chat</span><strong>{c.chat || 0}</strong></div>
        <div className="stat-card warn"><span>Filling details</span><strong>{c.profile || 0}</strong></div>
        <div className="stat-card"><span>Language test</span><strong>{c.language || 0}</strong></div>
        <div className="stat-card"><span>Listening test</span><strong>{c.listening || 0}</strong></div>
        <div className="stat-card"><span>Signing agreements</span><strong>{c.agreements || 0}</strong></div>
        <div className="stat-card success"><span>Shortlisted</span><strong data-testid="stat-shortlisted">{(c.shortlisted || 0) + (c.agreements || 0)}</strong></div>
      </div>
      <div className="creators-filters">
        <input placeholder="Search name, email or phone" value={search} onChange={e => setSearch(e.target.value)} data-testid="creator-search" />
        <select value={language} onChange={e => setLanguage(e.target.value)} data-testid="creator-language-filter"><option value="">All languages</option>{langs.map(l => <option key={l}>{l}</option>)}</select>
        <div className="chip-row">{STAGES.map(([k, l]) => <button key={k} className={`chip ${stage === k ? "active" : ""}`} onClick={() => setStage(k)} data-testid={`creator-stage-${k || "all"}`}>{l}</button>)}</div>
      </div>
      <div className="table-wrap card" style={{ padding: 0 }}>
        <table className="report-table" data-testid="creator-candidates-table">
          <thead><tr><th>Candidate</th><th>Languages</th><th>Chat done</th><th>Details complete</th><th>Language test</th><th>Listening test</th><th>Shortlisted</th><th>Agreements</th><th></th></tr></thead>
          <tbody>
            {data.items.map(r => (
              <tr key={r.id} onClick={() => setOpen(r.id)} data-testid={`creator-row-${r.id}`}>
                <td><strong>{r.name}</strong><br /><small>{r.email}{r.phone ? ` · ${r.phone}` : ""}</small></td>
                <td>{r.languages.join(" + ")}</td>
                <td><Tick ok={r.onboarding_done} testid={`creator-chat-${r.id}`} /></td>
                <td><Tick ok={r.profile_complete} testid={`creator-profile-${r.id}`} /></td>
                <td><TestCell items={r.language_tests} status={r.language_status} /></td>
                <td><TestCell items={r.listening_tests} status={r.listening_status} /></td>
                <td><Tick ok={r.shortlisted} label={r.shortlisted ? "Yes" : "No"} testid={`creator-shortlisted-${r.id}`} /></td>
                <td data-testid={`creator-agreements-${r.id}`}>{r.agreements.length ? <span className={`badge ${r.agreements_done ? "success" : r.shortlisted ? "warning" : ""}`}>{r.agreements.filter(a => a.signed).length}/{r.agreements.length} signed</span> : <small className="muted">—</small>}</td>
                <td><button className="pill-action neutral" onClick={e => { e.stopPropagation(); setOpen(r.id); }} data-testid={`view-creator-${r.id}`}><Eye size={13} /> View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.items.length === 0 && <div className="empty" style={{ border: "none" }} data-testid="creators-empty">No candidates in this view yet. Share the creator sign-in link to start receiving applications.</div>}
      </div>
      {open && <CandidateModal id={open} onClose={() => setOpen(null)} onChanged={load} />}
    </>
  );
}
